import { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';

import { ContractorOwner } from '@/types';
import { getContractorOwner, updateContractorOwner } from '@/utils/contractor_owner_utils';

export type ContractorOwnerFormPayload = {
    name: string;
    company_name?: string;
    email?: string;
    phone_number?: string;
};

type FormState = {
    name: string;
    company: string;
    email: string;
    phone: string;
};

const emptyForm = (): FormState => ({
    name: '',
    company: '',
    email: '',
    phone: '',
});

function saveErrorMessage(e: unknown, mode: 'create' | 'edit'): string {
    if (e instanceof Error && e.message) return e.message;
    return mode === 'create' ? 'Failed to create contractor' : 'Failed to update contractor';
}

export type ContractorOwnerFormDialogProps = {
    open: boolean;
    mode: 'create' | 'edit';
    contractor: ContractorOwner | null;
    label?: string;
    onClose: () => void;
    onSave: (payload: ContractorOwnerFormPayload) => Promise<ContractorOwner>;
};

export const ContractorOwnerFormDialog = ({
    open,
    mode,
    contractor,
    label = 'contractor',
    onClose,
    onSave,
}: ContractorOwnerFormDialogProps) => {
    const [form, setForm] = useState<FormState>(emptyForm);
    const [saving, setSaving] = useState(false);
    const [loadingEdit, setLoadingEdit] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setSaveError(null);
        if (mode === 'edit' && contractor != null) {
            setForm({
                name: contractor.name || '',
                company: contractor.company_name || '',
                email: contractor.email || '',
                phone: contractor.phone_number || '',
            });
            if (Number(contractor.id) > 0) {
                setLoadingEdit(true);
                getContractorOwner(Number(contractor.id))
                    .then(full => {
                        setForm({
                            name: full.name || '',
                            company: full.company_name || '',
                            email: full.email || '',
                            phone: full.phone_number || '',
                        });
                    })
                    .catch(() => {
                        // keep partial fields from selection
                    })
                    .finally(() => setLoadingEdit(false));
            }
        } else {
            setForm(emptyForm());
        }
    }, [open, mode, contractor]);

    const patchForm = (patch: Partial<FormState>) => {
        setSaveError(null);
        setForm(f => ({ ...f, ...patch }));
    };

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const handleSave = async () => {
        const name = form.name.trim();
        if (!name) {
            setSaveError('Name is required');
            return;
        }
        const payload: ContractorOwnerFormPayload = {
            name,
            company_name: form.company.trim(),
            email: form.email.trim() || undefined,
            phone_number: form.phone.trim() || undefined,
        };
        setSaving(true);
        setSaveError(null);
        try {
            await onSave(payload);
            onClose();
        } catch (e) {
            setSaveError(saveErrorMessage(e, mode));
        } finally {
            setSaving(false);
        }
    };

    const formDisabled = saving || loadingEdit;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {mode === 'edit' ? `Edit ${label}` : `Create ${label}`}
            </DialogTitle>
            <DialogContent>
                {loadingEdit ? (
                    <Stack alignItems="center" py={3}>
                        <CircularProgress size={24} />
                    </Stack>
                ) : (
                    <Stack spacing={2} pt={1}>
                        {saveError ? (
                            <Alert severity="error" sx={{ fontSize: 13 }}>
                                {saveError}
                            </Alert>
                        ) : null}
                        <TextField
                            label="Name"
                            value={form.name}
                            onChange={e => patchForm({ name: e.target.value })}
                            fullWidth
                            autoFocus
                            required
                            disabled={formDisabled}
                        />
                        <TextField
                            label="Last Name or Company Name"
                            value={form.company}
                            onChange={e => patchForm({ company: e.target.value })}
                            fullWidth
                            disabled={formDisabled}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={form.email}
                            onChange={e => patchForm({ email: e.target.value })}
                            fullWidth
                            disabled={formDisabled}
                        />
                        <TextField
                            label="Phone"
                            type="tel"
                            value={form.phone}
                            onChange={e => patchForm({ phone: e.target.value })}
                            fullWidth
                            disabled={formDisabled}
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={() => void handleSave()}
                    disabled={formDisabled}
                >
                    {saving
                        ? mode === 'edit'
                            ? 'Saving…'
                            : 'Creating…'
                        : mode === 'edit'
                          ? 'Save'
                          : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** Convenience wrapper for edit-only flows (e.g. Related People tab). */
export type ContractorOwnerEditDialogProps = {
    open: boolean;
    contractor: ContractorOwner | null;
    label?: string;
    onClose: () => void;
    onUpdated: (updated: ContractorOwner) => void;
};

export const ContractorOwnerEditDialog = ({
    open,
    contractor,
    label = 'contractor/owner',
    onClose,
    onUpdated,
}: ContractorOwnerEditDialogProps) => {
    if (contractor == null) return null;

    return (
        <ContractorOwnerFormDialog
            open={open}
            mode="edit"
            contractor={contractor}
            label={label}
            onClose={onClose}
            onSave={async payload => {
                const updated = await updateContractorOwner(Number(contractor.id), payload);
                onUpdated(updated);
                return updated;
            }}
        />
    );
};
